import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { FixingMarginType, FixingPriceType, ValidateFixingOrderRequest } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ValidateFixingOrderBody = {
  isGtc: boolean;
  bdrId: string;
  amountInCcy1: number | undefined;
  amountInCcy2: number | undefined;
  ccyPair: string;
  way: 'buy' | 'sell';
  base64Emails: string;
  localTimeWithOffSet: number;
  fixingBenchmark: string | undefined;
  fixingPriceType: FixingPriceType | undefined;
  fixingDateUtc?: string | undefined;
  expiryDay?: string;
  fixingTime?: string;
  fixingPlace?: string;
  marginInBps?: number | undefined;
  fixingMarginType: FixingMarginType | undefined;
};

export type ValidateOrderResponseError = {
  Status: number;
  Title: string;
};

export const validateFixing = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ValidateOrderResponse, ValidateFixingOrderRequest>({
    query: (request) => ({
      url: '/oms/validate/Fixing',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformErrorResponse: (baseQueryReturnValue) => {
      if (baseQueryReturnValue && typeof baseQueryReturnValue === 'object' && 'data' in baseQueryReturnValue) {
        if (typeof baseQueryReturnValue.data === 'string') {
          return JSON.parse(baseQueryReturnValue.data) as ValidateOrderResponse;
        }
        return baseQueryReturnValue.data as ValidateOrderResponse;
      }
    },
  });
};

const generateBody = (order: ValidateFixingOrderRequest): ValidateFixingOrderBody => {
  return {
    isGtc: true,
    bdrId: String(order.clientId),
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    way: order.way ?? 'buy',
    amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
    amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
    fixingBenchmark: order.fixingReference ? order.fixingReference : undefined,
    fixingPriceType: order.fixingPriceType ? order.fixingPriceType : undefined,
    fixingDateUtc: order.fixingDateUtc ? order.fixingDateUtc : undefined,
    expiryDay: order.expiryDay ? order.expiryDay : undefined,
    fixingMarginType: order.fixingMarginType ? order.fixingMarginType : undefined,
    fixingTime: order.fixingTime,
    marginInBps: order.commercialMargin ? order.commercialMargin : undefined,
    fixingPlace: order.fixingPlace,
    base64Emails: isDefined(order?.notificationEmails?.emails)
      ? toBase64(order?.notificationEmails.emails.join(';'))
      : '',
    localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
  };
};
